import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CheckVoucherPreview from "../../components/accounting/CheckVoucherPreview";
import { buildCheckVoucherPrintHtml } from "../../utils/checkVoucherHtml";
import { downloadCheckVoucherAttachmentsZip } from "../../utils/checkVoucherAttachmentsZip";
import { API_BASE_URL, authFetch } from "../../config/api";

const money = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const isForSoa = (value) => {
  if (value === true || value === 1) return true;
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "true" || normalized === "t" || normalized === "1";
};

const resolveSignatureUrl = (value) => {
  const url = String(value || "").trim();
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_BASE_URL.replace(/\/$/, "")}/${url.replace(/^\/+/, "")}`;
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const formatTrailEvent = (eventType) => {
  switch (String(eventType || "").toLowerCase()) {
    case "created":
      return "Created";
    case "updated":
      return "Updated";
    default:
      return String(eventType || "Updated");
  }
};

const getVoucherStatus = (voucher) => {
  if (String(voucher?.status || "").toLowerCase() === "deleted") {
    return {
      label: "Deleted",
      className: "bg-slate-200 text-slate-600 ring-1 ring-slate-300",
    };
  }

  if (String(voucher?.status || "").toLowerCase() === "cancelled") {
    return {
      label: "Cancelled",
      className: "bg-red-50 text-red-700 ring-1 ring-red-200",
    };
  }

  if (voucher?.checked_by_name || voucher?.checked_by_user_id) {
    return {
      label: "Checked",
      className: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
    };
  }

  if (voucher?.assigned_checker_name || voucher?.assigned_checker_user_id) {
    return {
      label: "In Review",
      className: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
    };
  }

  return {
    label: "Pending",
    className: "bg-slate-100 text-slate-700 ring-1 ring-slate-200",
  };
};

const isDeletedVoucher = (voucher) => String(voucher?.status || "").toLowerCase() === "deleted";

const getVoucherStatusKey = (voucher) => {
  if (isDeletedVoucher(voucher)) return "deleted";
  if (String(voucher?.status || "").toLowerCase() === "cancelled") return "cancelled";
  if (voucher?.checked_by_name || voucher?.checked_by_user_id) return "checked";
  if (voucher?.assigned_checker_name || voucher?.assigned_checker_user_id) return "in_review";
  return "pending";
};

const enrichVoucherRequestsWithAttachments = async (voucherData) => {
  const requests = Array.isArray(voucherData?.requests) ? voucherData.requests : [];
  if (!requests.length) return voucherData;

  try {
    const res = await authFetch(`${API_BASE_URL}/api/accounting/requests`, {
      credentials: "include",
    });
    const json = await readJsonResponse(res);
    if (!json.success || !Array.isArray(json.data)) return voucherData;

    const requestMap = new Map(json.data.map((request) => [String(request.id), request]));
    const enrichedRequests = requests.map((request) => {
      const fallbackRequest = requestMap.get(String(request.id));
      const fallbackAttachments = Array.isArray(fallbackRequest?.attachments) ? fallbackRequest.attachments : [];
      const currentAttachments = Array.isArray(request.attachments) ? request.attachments : [];

      return {
        ...(fallbackRequest || {}),
        ...request,
        due_date: request.due_date || fallbackRequest?.due_date || "",
        mode_of_payment: request.mode_of_payment || fallbackRequest?.mode_of_payment || "",
        attachments: currentAttachments.length ? currentAttachments : fallbackAttachments,
      };
    });

    return {
      ...voucherData,
      requests: enrichedRequests,
    };
  } catch (err) {
    console.error("Failed to enrich voucher requests with attachments", err);
    return voucherData;
  }
};

const renderRequestAttachmentsPanel = (requests = []) => {
  const requestsWithAttachments = (requests || []).filter(
    (request) => Array.isArray(request.attachments) && request.attachments.length > 0
  );

  if (!requestsWithAttachments.length) {
    return (
      <div className="mt-5 rounded-[22px] border border-dashed border-slate-200 bg-slate-50/80 p-4">
        <p className="text-sm font-semibold text-slate-800">Attached documents</p>
        <p className="mt-1 text-sm text-slate-600">No attached documents were uploaded for the linked requests.</p>
      </div>
    );
  }

  const totalFiles = requestsWithAttachments.reduce(
    (sum, request) => sum + (request.attachments?.length || 0),
    0
  );

  return (
    <div className="mt-5 rounded-[22px] border border-slate-200 bg-slate-50/80 p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-800">Attached documents</p>
          <p className="text-xs text-slate-500">Files uploaded with the linked requests.</p>
        </div>
        <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 ring-1 ring-slate-200">
          {totalFiles} file{totalFiles !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="mt-4 space-y-3">
        {requestsWithAttachments.map((request) => (
          <div key={request.id} className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-[0.12em] text-blue">
              {request.rfp_no || "Request"} ({request.attachments.length} file
              {request.attachments.length !== 1 ? "s" : ""})
            </div>
            <div className="space-y-2">
              {request.attachments.map((doc, docIndex) => (
                <a
                  key={doc.id || docIndex}
                  href={doc.file_url || "#"}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm transition hover:bg-blue-50"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-slate-900">{doc.file_name || "Attachment"}</p>
                    <p className="text-xs text-slate-500">
                      {(doc.file_type || "file").toString()} •{" "}
                      {doc.created_at ? new Date(doc.created_at).toLocaleString() : "Uploaded"}
                    </p>
                  </div>
                  <span className="text-xs font-semibold text-blue">Open</span>
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const readJsonResponse = async (res) => {
  const raw = await res.text();
  const contentType = res.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    return JSON.parse(raw);
  }

  try {
    return JSON.parse(raw);
  } catch {
    const preview = raw.trim().slice(0, 120);
    throw new Error(
      preview.startsWith("<!DOCTYPE") || preview.startsWith("<html")
        ? "API returned HTML instead of JSON. Check the backend URL or auth session."
        : "API returned an unexpected non-JSON response."
    );
  }
};

const getDateRange = (filter, customStartDate, customEndDate) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (filter) {
    case "today":
      return { start: today, end: new Date(today.getTime() + 24 * 60 * 60 * 1000) };
    case "week": {
      const dayOfWeek = today.getDay();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - dayOfWeek);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 7);
      return { start: startOfWeek, end: endOfWeek };
    }
    case "month": {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      return { start: startOfMonth, end: endOfMonth };
    }
    case "custom":
      if (customStartDate && customEndDate) {
        const start = new Date(customStartDate);
        const end = new Date(customEndDate);
        end.setDate(end.getDate() + 1);
        return { start, end };
      }
      return null;
    default:
      return null;
  }
};

const CheckVoucherList = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const currentUserId = Number(user?.user_id || user?.id || 0);
  const pageSizeOptions = [10, 20, 50, 100];
  const [vouchers, setVouchers] = useState([]);
  const [selectedVoucherId, setSelectedVoucherId] = useState("");
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("week");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [trailVoucher, setTrailVoucher] = useState(null);
  const [trailRows, setTrailRows] = useState([]);
  const [trailLoading, setTrailLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [zipLoading, setZipLoading] = useState(false);
  const [voucherTabFilter, setVoucherTabFilter] = useState("all");
  const [jobOrderFilter, setJobOrderFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [deletingVoucherId, setDeletingVoucherId] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const filteredVouchers = useMemo(() => {
    const query = search.trim().toLowerCase();
    const range = dateFilter === "all" ? null : getDateRange(dateFilter, customStartDate, customEndDate);

    return vouchers.filter((voucher) => {
      const statusKey = getVoucherStatusKey(voucher);
      const matchesCurrentUser =
        currentUserId &&
        (Number(voucher?.assigned_checker_user_id || 0) === currentUserId ||
          Number(voucher?.checked_by_user_id || 0) === currentUserId);

      if (voucherTabFilter === "my_checks" && !matchesCurrentUser) return false;
      if (
        ["all", "my_checks"].indexOf(voucherTabFilter) === -1 &&
        statusKey !== voucherTabFilter
      ) {
        return false;
      }
      if (jobOrderFilter !== "all") {
        const connectedJobOrders = Array.isArray(voucher.connected_job_orders) ? voucher.connected_job_orders : [];
        if (!connectedJobOrders.includes(jobOrderFilter)) return false;
      }

      const voucherDate = voucher.voucher_date || voucher.created_at;
      if (range && voucherDate) {
        const date = new Date(voucherDate);
        if (!(date >= range.start && date < range.end)) return false;
      }

      if (!query) return true;
      const checkedByName = voucher.assigned_checker_name || voucher.checked_by_name || "";
      return [
        voucher.voucher_no,
        voucher.payee,
        voucher.check_no,
        voucher.amount,
        checkedByName,
        ...(Array.isArray(voucher.connected_job_orders) ? voucher.connected_job_orders : []),
      ]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [currentUserId, customEndDate, customStartDate, dateFilter, jobOrderFilter, search, voucherTabFilter, vouchers]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, dateFilter, customStartDate, customEndDate, voucherTabFilter, jobOrderFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredVouchers.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStart = filteredVouchers.length ? (safeCurrentPage - 1) * pageSize + 1 : 0;
  const pageEnd = Math.min(safeCurrentPage * pageSize, filteredVouchers.length);
  const paginatedVouchers = filteredVouchers.slice(
    (safeCurrentPage - 1) * pageSize,
    safeCurrentPage * pageSize
  );

  useEffect(() => {
    if (currentPage !== safeCurrentPage) {
      setCurrentPage(safeCurrentPage);
    }
  }, [currentPage, safeCurrentPage]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/check-vouchers`, {
        credentials: "include",
      });
      const json = await readJsonResponse(res);
      if (!json.success) throw new Error(json.message || "Unable to load vouchers");
      const nextVouchers = json.data || [];
      setVouchers(nextVouchers);
      setSelectedVoucherId((currentId) => {
        if (currentId && nextVouchers.some((voucher) => String(voucher.id) === String(currentId))) {
          return currentId;
        }
        return nextVouchers[0]?.id || "";
      });
    } catch (err) {
      setError(err.message || "Unable to load vouchers");
    } finally {
      setLoading(false);
    }
  };

  const loadVoucherDetail = async (voucherId) => {
    if (!voucherId) {
      setSelectedVoucher(null);
      return;
    }
    setDetailLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/check-vouchers/${voucherId}`, {
        credentials: "include",
      });
      const json = await readJsonResponse(res);
      if (!json.success) throw new Error(json.message || "Unable to load voucher");
      const enrichedData = await enrichVoucherRequestsWithAttachments(json.data || null);
      setSelectedVoucher(enrichedData || null);
    } catch (err) {
      setSelectedVoucher(null);
      setError(err.message || "Unable to load voucher");
    } finally {
      setDetailLoading(false);
    }
  };

  const loadVoucherTrail = async (voucherId) => {
    if (!voucherId) {
      setTrailVoucher(null);
      setTrailRows([]);
      return;
    }

    setTrailLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/check-vouchers/${voucherId}/trail`, {
        credentials: "include",
      });
      const json = await readJsonResponse(res);
      if (!json.success) throw new Error(json.message || "Unable to load voucher trail");
      setTrailRows(Array.isArray(json.data) ? json.data : []);
    } catch (err) {
      setTrailRows([]);
      setError(err.message || "Unable to load voucher trail");
    } finally {
      setTrailLoading(false);
    }
  };

  const openEditVoucher = (voucher) => {
    if (!voucher) return;
    navigate(`/accounting/check-voucher/create?edit=${encodeURIComponent(voucher.id)}`);
  };

  const openTrailVoucher = async (voucher) => {
    if (!voucher) return;
    setTrailVoucher(voucher);
    setTrailRows([]);
    await loadVoucherTrail(voucher.id);
  };

  const openPreviewVoucher = (voucher) => {
    if (!voucher) return;
    const alreadySelected = String(selectedVoucherId) === String(voucher.id);
    setSelectedVoucherId(voucher.id);
    setPreviewOpen(true);
    setError("");
    setSelectedVoucher(null);
    // If this row is already selected, changing the ID does not trigger the
    // selectedVoucherId effect, so explicitly reload its detail for the modal.
    if (alreadySelected) {
      loadVoucherDetail(voucher.id);
    }
  };

  const promptDeleteVoucher = (voucher) => {
    if (!voucher) return;
    setDeleteTarget(voucher);
  };

  const confirmDeleteVoucher = async () => {
    const voucher = deleteTarget;
    if (!voucher) return;

    setDeletingVoucherId(voucher.id);
    setError("");
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/check-vouchers/${voucher.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const json = await readJsonResponse(res);
      if (!json.success) throw new Error(json.message || "Unable to delete voucher");

      setVouchers((prev) => prev.filter((v) => String(v.id) !== String(voucher.id)));
      if (String(selectedVoucherId) === String(voucher.id)) {
        setSelectedVoucherId("");
        setSelectedVoucher(null);
      }
      setDeleteTarget(null);
    } catch (err) {
      setError(err.message || "Unable to delete voucher");
    } finally {
      setDeletingVoucherId("");
    }
  };

  const closePreviewVoucher = () => {
    setPreviewOpen(false);
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    loadVoucherDetail(selectedVoucherId);
  }, [selectedVoucherId]);

  const selectedRequests = selectedVoucher?.requests || [];
  const selectedLines = selectedVoucher?.lines || [];
  const preparerSignatureUrl = resolveSignatureUrl(selectedVoucher?.preparer_signature_url);
  const checkedBySignatureUrl = resolveSignatureUrl(selectedVoucher?.checked_by_signature_url);
  const selectedStatus = getVoucherStatus(selectedVoucher);
  const requestPreviewTotal = selectedRequests.reduce((sum, request) => sum + Number(request.total_amount || 0), 0);
  const voucherTotal = filteredVouchers.reduce((sum, voucher) => sum + Number(voucher.amount || 0), 0);
  const checkedCount = filteredVouchers.filter((voucher) => getVoucherStatusKey(voucher) === "checked").length;
  const cancelledCount = filteredVouchers.filter((voucher) => getVoucherStatusKey(voucher) === "cancelled").length;
  const pendingCount = filteredVouchers.filter((voucher) =>
    ["pending", "in_review"].includes(getVoucherStatusKey(voucher))
  ).length;
  const voucherTabCounts = {
    all: vouchers.length,
    deleted: vouchers.filter((voucher) => getVoucherStatusKey(voucher) === "deleted").length,
    cancelled: vouchers.filter((voucher) => getVoucherStatusKey(voucher) === "cancelled").length,
    checked: vouchers.filter((voucher) => getVoucherStatusKey(voucher) === "checked").length,
    in_review: vouchers.filter((voucher) => getVoucherStatusKey(voucher) === "in_review").length,
    pending: vouchers.filter((voucher) => getVoucherStatusKey(voucher) === "pending").length,
    my_checks: vouchers.filter(
      (voucher) =>
        currentUserId &&
        (Number(voucher?.assigned_checker_user_id || 0) === currentUserId ||
          Number(voucher?.checked_by_user_id || 0) === currentUserId)
    ).length,
  };
  const jobOrderOptions = useMemo(() => {
    const jobOrders = new Set();

    vouchers.forEach((voucher) => {
      (voucher.connected_job_orders || []).forEach((jobOrder) => {
        if (String(jobOrder || "").trim()) {
          jobOrders.add(String(jobOrder).trim());
        }
      });
    });

    return Array.from(jobOrders).sort((a, b) => a.localeCompare(b));
  }, [vouchers]);
  const selectedVoucherRequestsLabel =
    selectedRequests.length === 1 ? "1 connected request" : `${selectedRequests.length} connected requests`;
  const selectedVoucherJobOrders = Array.isArray(selectedVoucher?.connected_job_orders)
    ? selectedVoucher.connected_job_orders
    : [];
  const selectedVoucherJobOrdersLabel =
    selectedVoucherJobOrders.length === 1
      ? selectedVoucherJobOrders[0]
      : selectedVoucherJobOrders.length > 1
        ? `${selectedVoucherJobOrders.length} connected JO(s)`
        : "No connected JO";

  const printVoucher = async () => {
    if (!selectedVoucher) return;
    const win = window.open("", "_blank");
    if (!win) return;

    const printableHtml = buildCheckVoucherPrintHtml({
      voucher: selectedVoucher,
      requests: selectedRequests,
      lines: selectedLines,
    });

    if (selectedVoucher.soa_file_path || selectedVoucher.soa_file_url) {
      win.document.write("<p style=\"font-family:Arial;padding:24px\">Preparing printable voucher and SOA…</p>");
      win.document.close();
      try {
        const response = await authFetch(`${API_BASE_URL}/api/accounting/check-vouchers/${selectedVoucher.id}/print-pdf`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ html: printableHtml }),
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.message || "Unable to prepare the voucher PDF");
        }
        const pdfUrl = URL.createObjectURL(await response.blob());
        win.location.replace(pdfUrl);
        win.setTimeout(() => {
          win.focus();
          win.print();
        }, 1200);
        win.addEventListener("beforeunload", () => URL.revokeObjectURL(pdfUrl), { once: true });
      } catch (error) {
        win.close();
        setError(error.message || "Unable to prepare the voucher PDF");
      }
      return;
    }

    win.document.open();
    win.document.write(printableHtml);
    win.document.close();

    const triggerPrint = () => {
      win.focus();
      win.print();
    };

    win.addEventListener(
      "load",
      () => {
        win.setTimeout(triggerPrint, 1500);
      },
      { once: true }
    );
  };

  const downloadAttachmentsZip = async () => {
    if (!selectedVoucher) return;
    setZipLoading(true);
    setError("");
    try {
      await downloadCheckVoucherAttachmentsZip({
        voucher: selectedVoucher,
        voucherNo: selectedVoucher.voucher_no,
        requests: selectedVoucher.requests || [],
        lines: selectedVoucher.lines || [],
      });
    } catch (err) {
      setError(err.message || "Unable to download attachments ZIP");
    } finally {
      setZipLoading(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Loading vouchers...</div>;
  }

  return (
    <div className="space-y-6 font-poppins">
      <div className="rounded-3xl border border-gray-100 bg-white/95 p-5 shadow-sm backdrop-blur-sm">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-3xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Accounting</p>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <h1 className="text-3xl font-semibold tracking-tight text-blue sm:text-4xl">Check Voucher List</h1>
              </div>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
                Filter vouchers, inspect request connections, and open a print-ready preview without leaving the list.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => navigate("/accounting/check-voucher/create")}
                className="rounded-xl bg-blue px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue/90"
              >
                Create Voucher
              </button>
              <button
                onClick={() => navigate("/accounting/check-vouchers/pending")}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Pending Queue
              </button>
            </div>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-2xl bg-slate-50 p-4">
              <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Filtered vouchers</div>
              <div className="mt-2 text-2xl font-semibold text-slate-900">{filteredVouchers.length}</div>
            </div>
            <div className="rounded-2xl bg-blue-50 p-4">
              <div className="text-xs uppercase tracking-[0.14em] text-blue-700">Total amount</div>
              <div className="mt-2 text-2xl font-semibold text-blue">₱{money(voucherTotal)}</div>
            </div>
            <div className="rounded-2xl bg-emerald-50 p-4">
              <div className="text-xs uppercase tracking-[0.14em] text-emerald-700">Checked</div>
              <div className="mt-2 text-2xl font-semibold text-emerald-700">{checkedCount}</div>
            </div>
            <div className="rounded-2xl bg-amber-50 p-4">
              <div className="text-xs uppercase tracking-[0.14em] text-amber-700">Pending</div>
              <div className="mt-2 text-2xl font-semibold text-amber-700">{pendingCount}</div>
            </div>
            <div className="rounded-2xl bg-red-50 p-4">
              <div className="text-xs uppercase tracking-[0.14em] text-red-700">Cancelled</div>
              <div className="mt-2 text-2xl font-semibold text-red-700">{cancelledCount}</div>
            </div>
          </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="rounded-3xl border border-gray-100 bg-white/95 p-5 shadow-sm">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-1 flex-col gap-3 2xl:flex-row 2xl:items-center">
              <div className="flex flex-1 flex-wrap items-center gap-3">
                <div className="relative min-w-[260px] flex-1">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">⌕</span>
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search voucher no, payee, check no..."
                    className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-blue focus:ring-2 focus:ring-blue/10"
                  />
                </div>
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-blue focus:ring-2 focus:ring-blue/10"
                >
                  <option value="all">All Dates</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="custom">Custom</option>
                </select>
                <select
                  value={jobOrderFilter}
                  onChange={(e) => setJobOrderFilter(e.target.value)}
                  className="min-w-[200px] rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-blue focus:ring-2 focus:ring-blue/10"
                >
                  <option value="all">All JO</option>
                  {jobOrderOptions.map((jobOrder) => (
                    <option key={jobOrder} value={jobOrder}>
                      {jobOrder}
                    </option>
                  ))}
                </select>
                {dateFilter === "custom" && (
                  <>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-blue focus:ring-2 focus:ring-blue/10"
                    />
                    <span className="text-sm text-slate-500">to</span>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-blue focus:ring-2 focus:ring-blue/10"
                    />
                  </>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={load}
                  className="rounded-2xl border border-slate-200 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-slate-700 transition hover:bg-slate-50"
                >
                  Refresh
                </button>
              </div>
            </div>
            <div className="text-sm font-medium text-slate-500">
              {filteredVouchers.length} voucher(s) found
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2 overflow-x-auto pb-1">
            {[
              { key: "all", label: "All", count: voucherTabCounts.all },
              { key: "deleted", label: "Deleted", count: voucherTabCounts.deleted },
              { key: "cancelled", label: "Cancelled", count: voucherTabCounts.cancelled },
              { key: "checked", label: "Checked", count: voucherTabCounts.checked },
              { key: "in_review", label: "In Review", count: voucherTabCounts.in_review },
              { key: "pending", label: "Pending", count: voucherTabCounts.pending },
              { key: "my_checks", label: "My Checks", count: voucherTabCounts.my_checks },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setVoucherTabFilter(tab.key)}
                className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition ${
                  voucherTabFilter === tab.key
                    ? "bg-blue text-white shadow-sm"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {tab.label}
                <span className="ml-2 inline-flex shrink-0 rounded-full bg-white/20 px-2 py-0.5 text-xs leading-none">
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
      </div>

      <div className="rounded-3xl border border-gray-100 bg-white/95 p-5 shadow-sm">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-blue">Voucher Table</h2>
            <p className="mt-1 text-sm text-slate-500">
              Select a voucher to open the existing Request Preview and Check Voucher Preview in a modal.
            </p>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
            {detailLoading ? "Loading" : selectedVoucher ? selectedStatus.label : "Idle"}
          </span>
        </div>

        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-slate-500">
            {filteredVouchers.length
              ? `Showing ${pageStart}-${pageEnd} of ${filteredVouchers.length} vouchers`
              : "No vouchers to display"}
          </div>
          <div className="flex items-center gap-2 text-sm">
            <label htmlFor="voucher-page-size" className="text-slate-500">
              Rows per page
            </label>
            <select
              id="voucher-page-size"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value) || 20);
                setCurrentPage(1);
              }}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue focus:ring-2 focus:ring-blue/10"
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[1480px] border-collapse text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="w-[170px] px-4 py-3">Voucher No</th>
                <th className="w-[220px] px-4 py-3">JO</th>
                <th className="w-[280px] px-4 py-3">Payee</th>
                <th className="w-[120px] px-4 py-3">Check No.</th>
                <th className="w-[140px] px-4 py-3">Voucher Date</th>
                <th className="w-[140px] px-4 py-3">Check Date</th>
                <th className="w-[180px] px-4 py-3">Assigned Checker</th>
                <th className="w-[100px] px-4 py-3">SOA</th>
                <th className="w-[140px] px-4 py-3 text-right">Amount</th>
                <th className="w-[170px] px-4 py-3">Status</th>
                <th className="w-[200px] px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginatedVouchers.map((voucher) => {
                const status = getVoucherStatus(voucher);
                return (
                  <tr
                    key={voucher.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => openPreviewVoucher(voucher)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openPreviewVoucher(voucher);
                      }
                    }}
                  >
                    <td className="w-[170px] px-4 py-3 font-semibold text-blue">{voucher.voucher_no || "—"}</td>
                    <td className="w-[220px] px-4 py-3 text-slate-700">
                      <div className="max-w-[220px] truncate" title={(voucher.connected_job_orders || []).join(", ") || "—"}>
                        {(voucher.connected_job_orders || []).length
                          ? voucher.connected_job_orders.length === 1
                            ? voucher.connected_job_orders[0]
                            : `${voucher.connected_job_orders.length} JO(s)`
                          : "—"}
                      </div>
                    </td>
                    <td className="w-[280px] px-4 py-3 text-slate-700">{voucher.payee || "—"}</td>
                    <td className="w-[120px] px-4 py-3 text-slate-700">{voucher.check_no || "—"}</td>
                    <td className="w-[140px] px-4 py-3 text-slate-700">{formatDate(voucher.voucher_date || voucher.created_at)}</td>
                    <td className="w-[140px] px-4 py-3 text-slate-700">{formatDate(voucher.check_date)}</td>
                    <td className="w-[180px] px-4 py-3 text-slate-700">
                      {voucher.assigned_checker_name || voucher.checked_by_name || "Pending"}
                    </td>
                    <td className="w-[100px] px-4 py-3">
                      <span
                        className={isForSoa(voucher.for_soa)
                          ? "rounded-full bg-green-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-green-700"
                          : "rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500"}
                      >
                        {isForSoa(voucher.for_soa) ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className="w-[140px] px-4 py-3 text-right font-semibold text-slate-900">₱{money(voucher.amount)}</td>
                    <td className="w-[170px] px-4 py-3">
                      <span
                        className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] ${status.className}`}
                      >
                        {status.label}
                      </span>
                    </td>
                    <td className="w-[200px] px-4 py-3 text-right">
                      {isDeletedVoucher(voucher) ? (
                        <div className="grid grid-cols-2 gap-1.5">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openPreviewVoucher(voucher);
                            }}
                            className="col-span-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-50"
                          >
                            View
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openTrailVoucher(voucher);
                            }}
                            className="col-span-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-50"
                          >
                            Trail
                          </button>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-1.5">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openPreviewVoucher(voucher);
                            }}
                            className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-50"
                          >
                            View
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditVoucher(voucher);
                            }}
                            className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-50"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openTrailVoucher(voucher);
                            }}
                            className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-50"
                          >
                            Trail
                          </button>
                          <button
                            type="button"
                            disabled={deletingVoucherId === voucher.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              promptDeleteVoucher(voucher);
                            }}
                            className="rounded-full border border-red-600 bg-red-600 px-1 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {deletingVoucherId === voucher.id ? "..." : "Delete"}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!paginatedVouchers.length && (
                <tr>
                  <td className="px-4 py-8 text-center text-sm text-slate-500" colSpan={10}>
                    No vouchers found. Adjust the search or date filter, or refresh the list.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-slate-500">
            Page {safeCurrentPage} of {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage(1)}
              disabled={safeCurrentPage === 1}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              First
            </button>
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={safeCurrentPage === 1}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              disabled={safeCurrentPage >= totalPages}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
            <button
              type="button"
              onClick={() => setCurrentPage(totalPages)}
              disabled={safeCurrentPage >= totalPages}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Last
            </button>
          </div>
        </div>
      </div>

      <div className="hidden">
        <div className="rounded-3xl border border-gray-100 bg-white/95 p-5 shadow-sm">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-blue">Voucher Queue</h2>
                <p className="mt-1 text-sm text-slate-500">Choose a voucher to inspect its request chain and printable detail.</p>
              </div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
                {detailLoading ? "Loading" : selectedVoucher ? selectedStatus.label : "Idle"}
              </span>
            </div>

            <div className="space-y-3">
              {filteredVouchers.map((voucher) => {
                const status = getVoucherStatus(voucher);
                const isActive = String(selectedVoucherId) === String(voucher.id);

                return (
                  <div
                    key={voucher.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedVoucherId(voucher.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedVoucherId(voucher.id);
                      }
                    }}
                    className={`w-full rounded-[22px] border p-4 text-left transition ${
                      isActive
                        ? "border-blue bg-blue-50 shadow-[0_8px_24px_rgba(37,99,235,0.12)]"
                        : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="text-base font-semibold text-blue">{voucher.voucher_no || "—"}</div>
                          <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] ${status.className}`}>
                            {status.label}
                          </span>
                        </div>
                        <div className="mt-2 grid grid-cols-1 gap-2 text-sm text-slate-600 sm:grid-cols-2">
                          <div><span className="font-medium text-slate-500">Payee:</span> {voucher.payee || "—"}</div>
                          <div><span className="font-medium text-slate-500">Check No.:</span> {voucher.check_no || "—"}</div>
                          <div><span className="font-medium text-slate-500">Date:</span> {formatDate(voucher.voucher_date || voucher.created_at)}</div>
                          <div><span className="font-medium text-slate-500">Checked by:</span> {voucher.assigned_checker_name || voucher.checked_by_name || "Pending"}</div>
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-2">
                        <div className="text-lg font-semibold text-slate-900">₱{money(voucher.amount)}</div>
                        <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                          {formatDate(voucher.check_date || voucher.voucher_date)}
                        </div>
                        <div className="flex flex-wrap justify-end gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditVoucher(voucher);
                            }}
                            className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-50"
                          >
                            Edit Voucher
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openTrailVoucher(voucher);
                            }}
                            className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-50"
                          >
                            Trail
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {!filteredVouchers.length && (
              <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-sm text-slate-500">
                No vouchers found. Adjust the search or date filter, or refresh the list.
              </div>
            )}
        </div>

        <div className="space-y-6 hidden">
            {selectedVoucher ? (
              <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[1.1fr_0.9fr]">
                <div className="space-y-6">
                  <div className="rounded-3xl border border-gray-100 bg-white/95 p-5 shadow-sm">
                    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h2 className="text-lg font-semibold text-blue">Request Preview</h2>
                        <p className="mt-1 text-sm text-slate-500">
                          {selectedRequests.length
                            ? `${selectedVoucherRequestsLabel} • ${selectedVoucherJobOrdersLabel}`
                            : "No connected requests loaded"}
                        </p>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] ${selectedStatus.className}`}>
                        {detailLoading ? "Loading..." : selectedStatus.label}
                      </span>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="rounded-2xl bg-slate-50 p-4">
                        <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Voucher no</div>
                        <div className="mt-1 text-base font-semibold text-slate-900">{selectedVoucher.voucher_no || "—"}</div>
                      </div>
                      <div className="rounded-2xl bg-slate-50 p-4">
                        <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Request total</div>
                        <div className="mt-1 text-base font-semibold text-slate-900">₱{money(requestPreviewTotal)}</div>
                      </div>
                      <div className="rounded-2xl bg-slate-50 p-4">
                        <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Payee</div>
                        <div className="mt-1 text-base font-semibold text-slate-900">{selectedVoucher.payee || "—"}</div>
                      </div>
                      <div className="rounded-2xl bg-slate-50 p-4">
                        <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Assigned checker</div>
                        <div className="mt-1 text-base font-semibold text-slate-900">{selectedVoucher.assigned_checker_name || "Pending"}</div>
                      </div>
                      <div className="rounded-2xl bg-slate-50 p-4 sm:col-span-2">
                        <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Connected JO</div>
                        <div className="mt-1 text-base font-semibold text-slate-900">
                          {selectedVoucherJobOrders.length
                            ? selectedVoucherJobOrders.join(", ")
                            : "No connected JO"}
                        </div>
                      </div>
                    </div>

                    {renderRequestAttachmentsPanel(selectedRequests)}

                    {/* <div className="mt-5 overflow-hidden rounded-[22px] border border-slate-200">
                      <table className="min-w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                            <th className="px-4 py-3">RFP No</th>
                            <th className="px-4 py-3">Job Order</th>
                            <th className="px-4 py-3">Payee</th>
                            <th className="px-4 py-3 text-right">Amount</th>
                            <th className="px-4 py-3">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {requestAuditRows.map((request, index) => (
                            <tr key={request.id || `${request.rfp_no}-${index}`} className="bg-white">
                              <td className="px-4 py-3 font-medium text-blue">{request.rfp_no}</td>
                              <td className="px-4 py-3 text-slate-700">{request.job_order}</td>
                              <td className="px-4 py-3 text-slate-700">{request.payee}</td>
                              <td className="px-4 py-3 text-right text-slate-900">₱{money(request.total_amount)}</td>
                              <td className="px-4 py-3 text-slate-600">{request.status}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div> */}

                    {/* <div className="mt-5 rounded-[22px] border border-slate-200 p-4">
                      <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Primary request</div>
                      <div className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                        <div><strong>RFP No:</strong> {requestPreviewPrimary?.rfp_no || "—"}</div>
                        <div><strong>Job Order:</strong> {requestPreviewPrimary?.job_order || "—"}</div>
                        <div><strong>Created:</strong> {formatDate(requestPreviewPrimary?.created_at)}</div>
                        <div><strong>Particulars:</strong> {requestPreviewPrimaryParticulars}</div>
                      </div>
                    </div> */}
                  </div>

                  {/* <div className="rounded-3xl border border-gray-100 bg-white/95 p-5 shadow-sm">
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <div>
                        <h2 className="text-lg font-semibold text-blue">Connected Requests</h2>
                        <p className="mt-1 text-sm text-slate-500">Audit trail and supporting rows for the selected voucher.</p>
                      </div>
                      <div className="text-sm font-medium text-slate-500">{selectedRequests.length} item(s)</div>
                    </div>

                    {requestAuditRows.length ? (
                      <div className="space-y-3">
                        {requestAuditRows.map((request, index) => (
                          <div
                            key={request.id || `${request.rfp_no}-${index}`}
                            className="rounded-[22px] border border-slate-200 bg-slate-50/60 p-4"
                          >
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <div>
                                <div className="text-sm font-semibold text-blue">{request.rfp_no}</div>
                                <div className="text-xs uppercase tracking-[0.14em] text-slate-500">{request.status}</div>
                              </div>
                              <div className="text-base font-semibold text-slate-900">₱{money(request.total_amount)}</div>
                            </div>
                            <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-slate-700 sm:grid-cols-2">
                              <div><strong>Job Order:</strong> {request.job_order}</div>
                              <div><strong>Payee:</strong> {request.payee}</div>
                              <div><strong>Created:</strong> {formatDate(request.created_at)}</div>
                              <div className="sm:col-span-2"><strong>Particulars:</strong> {request.particulars || "—"}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-6 text-sm text-slate-500">
                        No linked requests were loaded for this voucher.
                      </div>
                    )}
                  </div> */}
                </div>

                <div className="space-y-6">
                  <div className="rounded-3xl border border-gray-100 bg-white/95 p-5 shadow-sm">
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <div>
                        <h2 className="text-lg font-semibold text-blue">Check Voucher Preview</h2>
                        <p className="mt-1 text-sm text-slate-500">Printable layout and accounting entries.</p>
                      </div>
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
                        Printable
                      </span>
                    </div>

                    <CheckVoucherPreview
                      voucherNo={selectedVoucher.voucher_no}
                      voucherDate={formatDate(selectedVoucher.voucher_date || selectedVoucher.created_at)}
                      payee={selectedVoucher.payee}
                      checkNo={selectedVoucher.check_no}
                      checkDate={formatDate(selectedVoucher.check_date)}
                      details={[
                        {
                          label: "Tax Treatment",
                          value: selectedVoucher.tax_type
                            ? `${selectedVoucher.tax_type}${selectedVoucher.tax_rate ? ` (${selectedVoucher.tax_rate}%)` : ""}`
                            : "—",
                        },
                      ]}
                      amountLabel="Amount"
                      amount={selectedVoucher.amount}
                      amountWords={selectedVoucher.amount_words}
                      preparerName={selectedVoucher.preparer_name}
                      assignedCheckerName={selectedVoucher.assigned_checker_name}
                      checkedByName={selectedVoucher.checked_by_name}
                      approvedByName={selectedVoucher.approved_by_name}
                      receivedByName={selectedVoucher.received_by_name || "Pending"}
                      preparerSignatureUrl={preparerSignatureUrl}
                      checkedBySignatureUrl={checkedBySignatureUrl}
                      lines={selectedLines}
                      requests={selectedVoucher.requests || []}
                      forSoa={isForSoa(selectedVoucher.for_soa)}
                      soaRemarks={isForSoa(selectedVoucher.for_soa) ? selectedVoucher.soa_remarks : ""}
                      soaFileName={isForSoa(selectedVoucher.for_soa) ? selectedVoucher.soa_file_name : ""}
                      soaFileUrl={isForSoa(selectedVoucher.for_soa) ? selectedVoucher.soa_file_url : ""}
                    />

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <button
                        onClick={printVoucher}
                        className="rounded-xl bg-blue px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue/90"
                      >
                        Download / Print
                      </button>
                      <button
                        onClick={downloadAttachmentsZip}
                        disabled={zipLoading}
                        className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
                      >
                        {zipLoading ? "Preparing ZIP..." : "Download Packet ZIP"}
                      </button>
                      <button
                        onClick={() => openEditVoucher(selectedVoucher)}
                        className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                      >
                        Edit Voucher
                      </button>
                      <button
                        onClick={() => openTrailVoucher(selectedVoucher)}
                        className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                      >
                        Trail
                      </button>
                      <div className="text-sm text-slate-500">
                        {selectedStatus.label} voucher ready for print.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-3xl border border-dashed border-slate-200 bg-white/90 p-8 text-sm text-slate-500 shadow-sm">
                Select a voucher to see the request preview and printable layout.
              </div>
            )}

            {previewOpen &&
              ReactDOM.createPortal(
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm">
                  <div className="relative flex h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl">
                    <div className="border-b border-slate-100 px-6 py-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="text-lg font-semibold text-blue">
                            {selectedVoucher?.voucher_no || "Voucher Preview"}
                          </h3>
                          <p className="mt-1 text-sm text-slate-500">
                            {selectedVoucher ? "Request Preview and Check Voucher Preview" : "Loading voucher details..."}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={closePreviewVoucher}
                          className="text-slate-400 hover:text-slate-600"
                        >
                          <i className="fa-solid fa-xmark text-lg" />
                        </button>
                      </div>
                    </div>

                    <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50 p-4 lg:p-6">
                      {detailLoading && !selectedVoucher ? (
                        <div className="flex h-full items-center justify-center rounded-[24px] border border-dashed border-slate-200 bg-white text-sm text-slate-500">
                          Loading voucher preview...
                        </div>
                      ) : selectedVoucher ? (
                        <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[1.1fr_0.9fr]">
                          <div className="space-y-6">
                            <div className="rounded-3xl border border-gray-100 bg-white/95 p-5 shadow-sm">
                              <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                                <div>
                                  <h2 className="text-lg font-semibold text-blue">Request Preview</h2>
                                  <p className="mt-1 text-sm text-slate-500">
                                    {selectedRequests.length
                                      ? `${selectedVoucherRequestsLabel} • ${selectedVoucherJobOrdersLabel}`
                                      : "No connected requests loaded"}
                                  </p>
                                </div>
                                <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] ${selectedStatus.className}`}>
                                  {detailLoading ? "Loading..." : selectedStatus.label}
                                </span>
                              </div>

                              <div className="grid gap-3 sm:grid-cols-2">
                                <div className="rounded-2xl bg-slate-50 p-4">
                                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Voucher no</div>
                                  <div className="mt-1 text-base font-semibold text-slate-900">{selectedVoucher.voucher_no || "—"}</div>
                                </div>
                                <div className="rounded-2xl bg-slate-50 p-4">
                                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Request total</div>
                                  <div className="mt-1 text-base font-semibold text-slate-900">₱{money(requestPreviewTotal)}</div>
                                </div>
                                <div className="rounded-2xl bg-slate-50 p-4">
                                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Payee</div>
                                  <div className="mt-1 text-base font-semibold text-slate-900">{selectedVoucher.payee || "—"}</div>
                                </div>
                                <div className="rounded-2xl bg-slate-50 p-4">
                                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Due date</div>
                                  <div className="mt-1 text-base font-semibold text-slate-900">
                                    {selectedVoucher.requests?.[0]?.due_date
                                      ? formatDate(selectedVoucher.requests[0].due_date)
                                      : "—"}
                                  </div>
                                </div>
                                <div className="rounded-2xl bg-slate-50 p-4">
                                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Mode of payment</div>
                                  <div className="mt-1 text-base font-semibold text-slate-900">
                                    {selectedVoucher.requests?.[0]?.mode_of_payment || "—"}
                                  </div>
                                </div>
                                <div className="rounded-2xl bg-slate-50 p-4">
                                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Assigned checker</div>
                                  <div className="mt-1 text-base font-semibold text-slate-900">{selectedVoucher.assigned_checker_name || "Pending"}</div>
                                </div>
                                <div className="rounded-2xl bg-slate-50 p-4 sm:col-span-2">
                                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Connected JO</div>
                                  <div className="mt-1 text-base font-semibold text-slate-900">
                                    {selectedVoucherJobOrders.length
                                      ? selectedVoucherJobOrders.join(", ")
                                      : "No connected JO"}
                                  </div>
                                </div>
                              </div>

                              {renderRequestAttachmentsPanel(selectedRequests)}
                            </div>
                          </div>

                          <div className="space-y-6">
                            <div className="rounded-3xl border border-gray-100 bg-white/95 p-5 shadow-sm">
                              <div className="mb-4 flex items-start justify-between gap-3">
                                <div>
                                  <h2 className="text-lg font-semibold text-blue">Check Voucher Preview</h2>
                                  <p className="mt-1 text-sm text-slate-500">Printable layout and accounting entries.</p>
                                </div>
                                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
                                  Printable
                                </span>
                              </div>

                              <CheckVoucherPreview
                                voucherNo={selectedVoucher.voucher_no}
                                voucherDate={formatDate(selectedVoucher.voucher_date || selectedVoucher.created_at)}
                                payee={selectedVoucher.payee}
                                checkNo={selectedVoucher.check_no}
                                checkDate={formatDate(selectedVoucher.check_date)}
                                details={[
                                  {
                                    label: "Tax Treatment",
                                    value: selectedVoucher.tax_type
                                      ? `${selectedVoucher.tax_type}${selectedVoucher.tax_rate ? ` (${selectedVoucher.tax_rate}%)` : ""}`
                                      : "—",
                                  },
                                ]}
                                amountLabel="Amount"
                                amount={selectedVoucher.amount}
                                amountWords={selectedVoucher.amount_words}
                                preparerName={selectedVoucher.preparer_name}
                                assignedCheckerName={selectedVoucher.assigned_checker_name}
                                checkedByName={selectedVoucher.checked_by_name}
                                approvedByName={selectedVoucher.approved_by_name}
                                receivedByName={selectedVoucher.received_by_name || "Pending"}
                                preparerSignatureUrl={preparerSignatureUrl}
                                checkedBySignatureUrl={checkedBySignatureUrl}
                                lines={selectedLines}
                                requests={selectedVoucher.requests || []}
                                forSoa={isForSoa(selectedVoucher.for_soa)}
                                soaRemarks={isForSoa(selectedVoucher.for_soa) ? selectedVoucher.soa_remarks : ""}
                                soaFileName={isForSoa(selectedVoucher.for_soa) ? selectedVoucher.soa_file_name : ""}
                                soaFileUrl={isForSoa(selectedVoucher.for_soa) ? selectedVoucher.soa_file_url : ""}
                              />

                              <div className="mt-4 flex flex-wrap items-center gap-2">
                                <button
                                  onClick={printVoucher}
                                  className="rounded-xl bg-blue px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue/90"
                                >
                                  Download / Print
                                </button>
                                <button
                                  onClick={downloadAttachmentsZip}
                                  disabled={zipLoading}
                                  className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
                                >
                                  {zipLoading ? "Preparing ZIP..." : "Download Packet ZIP"}
                                </button>
                                <button
                                  onClick={() => openEditVoucher(selectedVoucher)}
                                  className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                                >
                                  Edit Voucher
                                </button>
                                <button
                                  onClick={() => openTrailVoucher(selectedVoucher)}
                                  className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                                >
                                  Trail
                                </button>
                                <div className="text-sm text-slate-500">
                                  {selectedStatus.label} voucher ready for print.
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex h-full items-center justify-center rounded-[24px] border border-dashed border-slate-200 bg-white text-sm text-slate-500">
                          Select a voucher to preview it.
                        </div>
                      )}
                    </div>
                  </div>
                </div>,
                document.body
              )}

            {trailVoucher &&
              ReactDOM.createPortal(
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm">
                  <div className="w-full max-w-2xl rounded-3xl bg-white p-6 shadow-2xl">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-semibold text-blue">Voucher Trail</h3>
                        <p className="mt-1 text-sm text-slate-500">
                          {trailVoucher.voucher_no || "Voucher"}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setTrailVoucher(null);
                          setTrailRows([]);
                        }}
                        className="text-slate-400 hover:text-slate-600"
                      >
                        <i className="fa-solid fa-xmark text-lg" />
                      </button>
                    </div>

                    <div className="mt-5 max-h-[60vh] space-y-3 overflow-y-auto pr-1">
                      {trailLoading ? (
                        <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-6 text-sm text-slate-500">
                          Loading trail...
                        </div>
                      ) : trailRows.length ? (
                        trailRows.map((entry) => (
                          <div key={entry.id} className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <div className="text-sm font-semibold text-slate-900">
                                  {formatTrailEvent(entry.event_type)}
                                </div>
                                <div className="text-xs text-slate-500">{entry.summary || "—"}</div>
                              </div>
                              <div className="text-right text-xs text-slate-500">
                                <div>{entry.actor_name || "System"}</div>
                                <div>{formatDateTime(entry.created_at)}</div>
                              </div>
                            </div>
                            {entry.before_state || entry.after_state ? (
                              <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-slate-600 sm:grid-cols-2">
                                <div className="rounded-xl bg-white px-3 py-2 ring-1 ring-slate-200">
                                  <span className="font-semibold text-slate-700">Before:</span>{" "}
                                  {entry.before_state?.check_date || "—"}
                                </div>
                                <div className="rounded-xl bg-white px-3 py-2 ring-1 ring-slate-200">
                                  <span className="font-semibold text-slate-700">After:</span>{" "}
                                  {entry.after_state?.check_date || "—"}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        ))
                      ) : (
                        <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-6 text-sm text-slate-500">
                          No trail entries found for this voucher.
                        </div>
                      )}
                    </div>
                  </div>
                </div>,
                document.body
              )}

            {deleteTarget &&
              ReactDOM.createPortal(
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm">
                  <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">Delete voucher</h3>
                        <p className="mt-1 text-sm text-slate-500">
                          This action cannot be undone.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(null)}
                        disabled={!!deletingVoucherId}
                        className="text-slate-400 hover:text-slate-600 disabled:opacity-40"
                      >
                        <i className="fa-solid fa-xmark text-lg" />
                      </button>
                    </div>

                    <div className="mt-4 rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700">
                      Are you sure you want to delete voucher{" "}
                      <span className="font-semibold">{deleteTarget.voucher_no || "—"}</span>
                      {deleteTarget.payee ? (
                        <>
                          {" "}for <span className="font-semibold">{deleteTarget.payee}</span>
                        </>
                      ) : null}
                      ? All accounting entries linked to this voucher will be permanently removed.
                    </div>

                    <div className="mt-5 flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(null)}
                        disabled={!!deletingVoucherId}
                        className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={confirmDeleteVoucher}
                        disabled={!!deletingVoucherId}
                        className="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {deletingVoucherId ? "Deleting..." : "Delete Voucher"}
                      </button>
                    </div>
                  </div>
                </div>,
                document.body
              )}
        </div>
      </div>
    </div>
  );
};

export default CheckVoucherList;
